import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const OPTION_CARD_SELECTOR = '[data-wowrio-option-card]';
const NATIVE_OPTION_SELECTOR =
  'input[data-option-name], option[data-option-name], [data-wowrio-native-single-option][data-option-name]';
const NATIVE_GROUP_HIDDEN_ATTRIBUTE = 'data-wowrio-native-option-group-hidden';
const NATIVE_PICKER_HIDDEN_ATTRIBUTE = 'data-wowrio-native-picker-hidden';

/** @typedef {HTMLInputElement | HTMLOptionElement | HTMLElement} WowrioNativeOption */

class WowrioOptionCardEnhancer extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Element | Document | null} */
  #scope;

  /** @type {HTMLElement | null} */
  #nativePicker;

  /** @type {boolean | null} */
  #currentVariantAvailable = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#scope = this.closest('.shopify-section') || this.closest('section') || document;
    this.#currentVariantAvailable = this.#readInitialVariantAvailability();

    this.addEventListener('click', this.#handleCardClick, { signal: this.#abortController.signal });
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });

    this.#refreshNativePicker();
    this.#bindNativePicker();
    this.#syncCards();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleCardClick = (event) => {
    const card = event.target instanceof Element ? event.target.closest(OPTION_CARD_SELECTOR) : null;

    if (!(card instanceof HTMLButtonElement) || card.disabled) return;
    if (!this.#canProxyOptions()) return;

    const nativeOption = this.#findNativeOptionForCard(card);

    if (!nativeOption || this.#isNativeOptionUnavailable(nativeOption)) {
      this.#announce('This option is not available.');
      this.#syncCards();
      return;
    }

    const nativeGroup = this.#getNativeOptionGroup(nativeOption);
    const nativeGroupIsHidden =
      nativeGroup?.hasAttribute(NATIVE_GROUP_HIDDEN_ATTRIBUTE) ||
      this.#nativePicker?.hasAttribute(NATIVE_PICKER_HIDDEN_ATTRIBUTE);

    if (this.#isNativeSingletonOption(nativeOption)) {
      this.#syncCards();
      return;
    }

    if (nativeOption instanceof HTMLInputElement) {
      if (!nativeGroupIsHidden) {
        nativeOption.focus({ preventScroll: true });
      }
      nativeOption.click();
      return;
    }

    if (nativeOption instanceof HTMLOptionElement && nativeOption.parentElement instanceof HTMLSelectElement) {
      nativeOption.parentElement.value = nativeOption.value;

      if (!nativeGroupIsHidden) {
        nativeOption.parentElement.focus({ preventScroll: true });
      }

      nativeOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #handleProductSelect = (event) => {
    const productSelectEvent = /** @type {CustomEvent & { promise?: Promise<unknown> }} */ (event);

    if (productSelectEvent.promise && typeof productSelectEvent.promise.finally === 'function') {
      productSelectEvent.promise
        .then((result) => {
          this.#currentVariantAvailable = result?.detail?.resource?.available === true;
        })
        .finally(() => {
          window.requestAnimationFrame(() => this.#refreshAfterNativeUpdate());
        });
      return;
    }

    window.requestAnimationFrame(() => this.#refreshAfterNativeUpdate());
  };

  #handleNativeChange = () => {
    window.requestAnimationFrame(() => this.#syncCards());
  };

  #refreshAfterNativeUpdate() {
    const previousPicker = this.#nativePicker;

    this.#refreshNativePicker();

    if (this.#nativePicker && this.#nativePicker !== previousPicker) {
      this.#bindNativePicker();
    }

    this.#syncCards();
  }

  #refreshNativePicker() {
    const productId = this.dataset.productId;
    const scope = this.#scope || document;

    if (productId && window.CSS?.escape) {
      this.#nativePicker =
        scope.querySelector(`variant-picker[data-product-id="${CSS.escape(productId)}"]`) ||
        scope.querySelector('variant-picker');
    } else {
      this.#nativePicker = scope.querySelector('variant-picker');
    }

    this.toggleAttribute('data-wowrio-native-picker-found', Boolean(this.#nativePicker));
  }

  #bindNativePicker() {
    this.#nativePicker?.addEventListener('change', this.#handleNativeChange, {
      signal: this.#abortController?.signal,
    });
  }

  #syncCards() {
    const cards = Array.from(this.querySelectorAll(OPTION_CARD_SELECTOR)).filter(
      (card) => card instanceof HTMLButtonElement
    );
    const hideUnmatched = this.dataset.wowrioHideUnmatched === 'true';
    let selectedTitle = '';
    const bindings = this.#getCardBindings(cards);

    for (const binding of bindings) {
      const { card, nativeOption } = binding;
      const unavailable = !nativeOption || this.#isNativeOptionUnavailable(nativeOption);
      const selected = nativeOption ? this.#isNativeOptionSelected(nativeOption) : false;

      card.toggleAttribute('hidden', hideUnmatched && !nativeOption);
      card.disabled = unavailable;
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
      card.toggleAttribute('data-wowrio-option-selected', selected);
      card.toggleAttribute('data-wowrio-option-unavailable', unavailable);

      if (selected) {
        selectedTitle = card.dataset.wowrioCardTitle || card.dataset.wowrioOptionValue || '';
      }
    }

    this.#syncNativePickerDisplay();

    if (selectedTitle) {
      this.#announce(`${selectedTitle} selected.`);
    }
  }

  /**
   * @param {HTMLButtonElement[]} cards
   * @returns {{ card: HTMLButtonElement, optionName: string, optionValue: string, nativeOption: WowrioNativeOption | null }[]}
   */
  #getCardBindings(cards) {
    return cards.map((card) => {
      return {
        card,
        optionName: this.#getCardOptionName(card),
        optionValue: this.#normalize(card.dataset.wowrioOptionValue),
        nativeOption: this.#findNativeOptionForCard(card),
      };
    });
  }

  /**
   * @param {HTMLButtonElement} card
   * @returns {WowrioNativeOption | null}
   */
  #findNativeOptionForCard(card) {
    this.#refreshNativePicker();

    if (!this.#nativePicker) return null;

    const targetOptionName = this.#getCardOptionName(card);
    const targetOptionValue = this.#normalize(card.dataset.wowrioOptionValue);

    if (!targetOptionName || !targetOptionValue) return null;

    const nativeOptions = Array.from(this.#nativePicker.querySelectorAll(NATIVE_OPTION_SELECTOR));

    return (
      nativeOptions.find((nativeOption) => {
        if (!this.#isNativeOptionElement(nativeOption)) return false;

        return (
          this.#normalize(nativeOption.dataset.optionName) === targetOptionName &&
          this.#getNativeOptionValue(nativeOption) === targetOptionValue
        );
      }) || null
    );
  }

  /**
   * @param {{ card: HTMLButtonElement, optionName: string, optionValue: string, nativeOption: WowrioNativeOption | null }[]} bindings
   */
  #decorateNativeOptions(bindings) {
    if (!this.#canDecorateOptions() || !this.#nativePicker) return;

    for (const binding of bindings) {
      const { nativeOption } = binding;
      const nativeLabel = nativeOption instanceof HTMLInputElement ? nativeOption.closest('label') : null;

      nativeLabel?.setAttribute('data-wowrio-native-option-enhanced', 'true');
    }
  }

  #clearNativeDisplayEnhancements() {
    if (!this.#nativePicker) return;

    this.#nativePicker.removeAttribute(NATIVE_PICKER_HIDDEN_ATTRIBUTE);
    this.#nativePicker.removeAttribute('aria-hidden');

    for (const element of this.#nativePicker.querySelectorAll(
      `[${NATIVE_GROUP_HIDDEN_ATTRIBUTE}], [data-wowrio-native-option-enhanced]`
    )) {
      element.removeAttribute(NATIVE_GROUP_HIDDEN_ATTRIBUTE);
      element.removeAttribute('data-wowrio-native-option-enhanced');
      element.removeAttribute('aria-hidden');
    }
  }

  #syncNativePickerDisplay() {
    if (!this.#nativePicker) return;

    const enhancers = this.#getPeerEnhancers();
    const nativeGroups = this.#getNativeOptionGroups();
    const enhancerStates = enhancers.map((enhancer) => {
      enhancer.#refreshNativePicker();

      return {
        enhancer,
        displayMode: enhancer.dataset.wowrioNativePickerDisplayMode || 'hide_matched_option_groups',
        bindings: enhancer.#getCurrentBindings(),
      };
    });

    const allBindings = enhancerStates.flatMap((state) => state.bindings);
    const enhancedGroupNames = new Set();

    this.#clearNativeDisplayEnhancements();

    for (const state of enhancerStates) {
      state.enhancer.#decorateNativeOptions(state.bindings);
    }

    for (const group of nativeGroups) {
      if (this.#isNativeOptionGroupFullyEnhanced(group, allBindings)) {
        enhancedGroupNames.add(group.name);
      }
    }

    const allGroupsEnhanced = nativeGroups.length > 0 && enhancedGroupNames.size === nativeGroups.length;
    const shouldHideEntirePicker =
      allGroupsEnhanced &&
      enhancerStates.some((state) => state.displayMode === 'hide_entire_picker_when_all_groups_enhanced');

    this.#nativePicker.toggleAttribute(NATIVE_PICKER_HIDDEN_ATTRIBUTE, shouldHideEntirePicker);

    if (shouldHideEntirePicker) {
      this.#nativePicker.setAttribute('aria-hidden', 'true');
      return;
    }

    for (const group of nativeGroups) {
      const groupCanHide = enhancedGroupNames.has(group.name);
      const shouldHideGroup =
        groupCanHide &&
        enhancerStates.some(
          (state) =>
            state.displayMode === 'hide_matched_option_groups' &&
            state.bindings.some((binding) => binding.optionName === group.name)
        );

      for (const element of group.elements) {
        element.toggleAttribute(NATIVE_GROUP_HIDDEN_ATTRIBUTE, shouldHideGroup);

        if (shouldHideGroup) {
          element.setAttribute('aria-hidden', 'true');
        } else {
          element.removeAttribute('aria-hidden');
        }
      }
    }
  }

  /**
   * @returns {WowrioOptionCardEnhancer[]}
   */
  #getPeerEnhancers() {
    const scope = this.#scope || document;

    return Array.from(scope.querySelectorAll('wowrio-option-card-enhancer')).filter(
      (enhancer) => enhancer instanceof WowrioOptionCardEnhancer
    );
  }

  /**
   * @returns {{ card: HTMLButtonElement, optionName: string, optionValue: string, nativeOption: WowrioNativeOption | null }[]}
   */
  #getCurrentBindings() {
    const cards = Array.from(this.querySelectorAll(OPTION_CARD_SELECTOR)).filter(
      (card) => card instanceof HTMLButtonElement
    );

    return this.#getCardBindings(cards);
  }

  /**
   * @returns {{ name: string, values: Set<string>, elements: Set<HTMLElement>, options: WowrioNativeOption[] }[]}
   */
  #getNativeOptionGroups() {
    if (!this.#nativePicker) return [];

    const groups = new Map();
    const nativeOptions = Array.from(this.#nativePicker.querySelectorAll(NATIVE_OPTION_SELECTOR));

    for (const nativeOption of nativeOptions) {
      if (!this.#isNativeOptionElement(nativeOption)) continue;

      const optionName = this.#normalize(nativeOption.dataset.optionName);
      const optionValue = this.#getNativeOptionValue(nativeOption);
      const groupElement = this.#getNativeOptionGroup(nativeOption);

      if (!optionName || !optionValue || !groupElement) continue;

      if (!groups.has(optionName)) {
        groups.set(optionName, {
          name: optionName,
          values: new Set(),
          elements: new Set(),
          options: [],
        });
      }

      const group = groups.get(optionName);
      group.values.add(optionValue);
      group.elements.add(groupElement);
      group.options.push(nativeOption);
    }

    return Array.from(groups.values());
  }

  /**
   * @param {WowrioNativeOption} nativeOption
   * @returns {HTMLElement | null}
   */
  #getNativeOptionGroup(nativeOption) {
    if (this.#isNativeSingletonOption(nativeOption)) {
      return nativeOption;
    }

    if (nativeOption instanceof HTMLInputElement) {
      return nativeOption.closest('fieldset') || nativeOption.closest('.variant-option');
    }

    return nativeOption.parentElement?.closest('.variant-option') || nativeOption.parentElement;
  }

  /**
   * @param {{ name: string, values: Set<string>, elements: Set<HTMLElement>, options: WowrioNativeOption[] }} group
   * @param {{ card: HTMLButtonElement, optionName: string, optionValue: string, nativeOption: WowrioNativeOption | null }[]} bindings
   * @returns {boolean}
   */
  #isNativeOptionGroupFullyEnhanced(group, bindings) {
    const groupBindings = bindings.filter((binding) => binding.optionName === group.name);

    if (!groupBindings.length) return false;
    if (groupBindings.some((binding) => !binding.optionValue || !binding.nativeOption)) return false;

    const enhancedValues = new Set(groupBindings.map((binding) => binding.optionValue));

    for (const nativeValue of group.values) {
      if (!enhancedValues.has(nativeValue)) return false;
    }

    return true;
  }

  /**
   * @param {WowrioNativeOption} nativeOption
   * @returns {boolean}
   */
  #isNativeOptionSelected(nativeOption) {
    if (this.#isNativeSingletonOption(nativeOption)) {
      return true;
    }

    if (nativeOption instanceof HTMLInputElement) {
      return nativeOption.checked;
    }

    return nativeOption.selected || nativeOption.parentElement?.value === nativeOption.value;
  }

  /**
   * @param {WowrioNativeOption} nativeOption
   * @returns {boolean}
   */
  #isNativeOptionUnavailable(nativeOption) {
    if (this.#isNativeSingletonOption(nativeOption)) {
      if (this.#currentVariantAvailable !== null) {
        return !this.#currentVariantAvailable;
      }

      return nativeOption.dataset.optionAvailable === 'false' || nativeOption.getAttribute('aria-disabled') === 'true';
    }

    if (nativeOption instanceof HTMLInputElement) {
      return nativeOption.disabled || nativeOption.getAttribute('aria-disabled') === 'true';
    }

    return nativeOption.disabled;
  }

  /**
   * @param {Element} nativeOption
   * @returns {nativeOption is WowrioNativeOption}
   */
  #isNativeOptionElement(nativeOption) {
    return (
      nativeOption instanceof HTMLInputElement ||
      nativeOption instanceof HTMLOptionElement ||
      this.#isNativeSingletonOption(nativeOption)
    );
  }

  /**
   * @param {Element} nativeOption
   * @returns {nativeOption is HTMLElement}
   */
  #isNativeSingletonOption(nativeOption) {
    return nativeOption instanceof HTMLElement && nativeOption.hasAttribute('data-wowrio-native-single-option');
  }

  /**
   * @param {WowrioNativeOption} nativeOption
   * @returns {string}
   */
  #getNativeOptionValue(nativeOption) {
    if (this.#isNativeSingletonOption(nativeOption)) {
      return this.#normalize(nativeOption.dataset.optionValue);
    }

    return this.#normalize(nativeOption.value);
  }

  /**
   * @returns {boolean | null}
   */
  #readInitialVariantAvailability() {
    if (this.dataset.wowrioCurrentVariantAvailable === 'true') return true;
    if (this.dataset.wowrioCurrentVariantAvailable === 'false') return false;

    return null;
  }

  /**
   * @param {HTMLButtonElement} card
   * @returns {string}
   */
  #getCardOptionName(card) {
    return this.#normalize(card.dataset.wowrioOptionName || this.dataset.wowrioOptionTarget);
  }

  /**
   * @returns {boolean}
   */
  #canProxyOptions() {
    return this.dataset.wowrioBehavior === 'proxy_cards' || this.dataset.wowrioBehavior === 'proxy_and_decorate';
  }

  /**
   * @returns {boolean}
   */
  #canDecorateOptions() {
    return this.dataset.wowrioBehavior === 'decorate_native' || this.dataset.wowrioBehavior === 'proxy_and_decorate';
  }

  /**
   * @param {string | undefined} value
   * @returns {string}
   */
  #normalize(value) {
    return String(value || '').trim();
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const status = this.querySelector('[data-wowrio-option-card-status]');

    if (status) {
      status.textContent = message;
    }
  }
}

if (!customElements.get('wowrio-option-card-enhancer')) {
  customElements.define('wowrio-option-card-enhancer', WowrioOptionCardEnhancer);
}

const EXTRA_LINE_SELECTOR = '[data-wowrio-extra-line]';
const EXTRA_TOGGLE_SELECTOR = '[data-wowrio-extra-toggle]';
const EXTRA_VARIANT_SELECT_SELECTOR = '[data-wowrio-extra-variant-select]';
const SETUP_PRESETS_SELECTOR = 'wowrio-setup-presets';
const SETUP_CARD_SELECTOR = '[data-wowrio-setup-card]';
const SETUP_SELECTED_ATTRIBUTE = 'data-wowrio-setup-selected';
const SETUP_EXTRA_LINE_SELECTOR = '[data-wowrio-setup-extra-line]';
const SETUP_PROPERTY_INPUT_SELECTOR = '[data-wowrio-setup-property-input]';
const PRODUCT_FORM_SELECTOR = 'product-form-component form[data-type="add-to-cart-form"]';
const ACCELERATED_CHECKOUT_SELECTOR = '[ref="acceleratedCheckoutButtonContainer"]';
const MERCHANDISING_CHANGE_EVENT = 'wowrio:merchandising-change';
const GLOBAL_LISTENER_FLAG = '__wowrioNativeProductEnhancementsListenersAttached';
const WOWRIO_MODAL_LISTENER_FLAG = '__wowrioModalListenersAttached';
const WOWRIO_MODAL_SELECTOR = '[data-wowrio-modal]';
const WOWRIO_MODAL_TRIGGER_SELECTOR = '[data-wowrio-modal-trigger]';
const WOWRIO_MODAL_CLOSE_SELECTOR = '[data-wowrio-modal-close]';
const WOWRIO_MODAL_PANEL_SELECTOR = '[data-wowrio-modal-panel]';
const WOWRIO_MODAL_SCROLL_LOCK_CLASS = 'wowrio-modal-scroll-locked';
const wowrioModalState = new WeakMap();
const openWowrioModals = new Set();

class WowrioExtraProductCard extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.addEventListener('change', this.#handleChange, { signal: this.#abortController.signal });
    this.#syncSelectedVariant();
    this.#syncState();
    syncCarePromotionDisplay(this);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleChange = () => {
    this.#syncSelectedVariant();
    this.#syncState();
    syncCarePromotionDisplay(this);
    notifyMerchandisingChanged(this);
  };

  #syncSelectedVariant() {
    const select = this.querySelector(EXTRA_VARIANT_SELECT_SELECTOR);
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions[0];
    if (!option) return;

    this.dataset.wowrioExtraVariantId = option.value;
    this.dataset.wowrioExtraAvailable = option.dataset.wowrioExtraAvailable || 'false';

    const priceElement = this.querySelector('[data-wowrio-extra-price]');
    if (priceElement && option.dataset.wowrioExtraPriceDisplay) {
      priceElement.textContent = option.dataset.wowrioExtraPriceDisplay;
    }
  }

  #syncState() {
    const toggle = this.querySelector(EXTRA_TOGGLE_SELECTOR);
    const isAvailable = getBoolean(this.dataset.wowrioExtraAvailable);
    const isLocked = getBoolean(this.dataset.wowrioExtraLocked);

    if (toggle instanceof HTMLInputElement) {
      if (this.dataset.wowrioCareMode === 'included_product' && isAvailable) {
        toggle.checked = true;
      }

      toggle.disabled = isLocked || !isAvailable;
    }

    const selected = isExtraLineSelected(this);
    this.toggleAttribute('data-wowrio-extra-selected', selected);
    this.toggleAttribute('data-wowrio-extra-unavailable', !isAvailable);
  }
}

if (!customElements.get('wowrio-extra-product-card')) {
  customElements.define('wowrio-extra-product-card', WowrioExtraProductCard);
}

/**
 * Keeps the Care+ promotion mirror presentation-only. Real variant prices and
 * cart-line data remain unchanged for Shopify to price authoritatively.
 *
 * @param {HTMLElement} lineElement
 */
function syncCarePromotionDisplay(lineElement) {
  if (lineElement.dataset.wowrioExtraType !== 'care') return;

  const promotion = getCarePromotionState(lineElement);
  const moneyFormat = lineElement.dataset.wowrioMoneyFormat || '${{amount}}';
  const standardPrice = lineElement.querySelector('[data-wowrio-care-price-standard]');
  const originalPrice = lineElement.querySelector('[data-wowrio-care-price-original]');
  const promotionalPrice = lineElement.querySelector('[data-wowrio-care-price-promotional]');
  const promotionLabel = lineElement.querySelector('[data-wowrio-care-promotion-label]');
  const promotionNote = lineElement.querySelector('[data-wowrio-care-promotion-note]');

  lineElement.toggleAttribute('data-wowrio-care-promotion-active', promotion.active);

  if (standardPrice instanceof HTMLElement) {
    standardPrice.hidden = promotion.active;
  }

  if (originalPrice instanceof HTMLElement) {
    originalPrice.hidden = !promotion.active;
    originalPrice.textContent = formatRollbackMoney(promotion.originalPrice, moneyFormat);
  }

  if (promotionalPrice instanceof HTMLElement) {
    promotionalPrice.hidden = !promotion.active;
    promotionalPrice.textContent = promotion.mode === 'free'
      ? 'FREE'
      : formatRollbackMoney(promotion.displayPrice, moneyFormat);
  }

  if (promotionLabel instanceof HTMLElement) {
    const showLabel = promotion.active && (promotion.mode === 'percentage' || promotion.hasCustomLabel);
    promotionLabel.hidden = !showLabel;
    promotionLabel.textContent = showLabel ? promotion.label : '';
  }

  if (promotionNote instanceof HTMLElement) {
    promotionNote.hidden = !promotion.active || !promotion.note;
    promotionNote.textContent = promotion.active ? promotion.note : '';
  }
}

/**
 * @param {HTMLElement} lineElement
 * @param {number} [originalPriceOverride]
 */
function getCarePromotionState(lineElement, originalPriceOverride) {
  const modeValue = String(lineElement.dataset.wowrioCarePromotionMode || 'off').trim();
  const mode = modeValue === 'percentage' || modeValue === 'free' ? modeValue : 'off';
  const selectedOption = lineElement.querySelector(EXTRA_VARIANT_SELECT_SELECTOR);
  const optionPrice = selectedOption instanceof HTMLSelectElement
    ? Number(selectedOption.selectedOptions[0]?.dataset.wowrioExtraPrice)
    : Number.NaN;
  const datasetPrice = Number(lineElement.dataset.wowrioExtraPrice);
  const resolvedPrice = Number.isFinite(optionPrice) ? optionPrice : datasetPrice;
  const originalPrice = Math.max(
    0,
    Math.round(Number.isFinite(originalPriceOverride) ? originalPriceOverride : resolvedPrice || 0)
  );
  const percentage = Math.min(
    100,
    Math.max(1, Math.round(Number(lineElement.dataset.wowrioCarePromotionPercentage) || 1))
  );
  const quantity = parseQuantity(lineElement.dataset.wowrioExtraQuantity, 1);
  const maxQuantity = Math.max(1, Math.round(Number(lineElement.dataset.wowrioCarePromotionMaxQuantity) || 1));
  const selected = isExtraLineSelected(lineElement);
  const available = getBoolean(lineElement.dataset.wowrioExtraAvailable);
  const eligible = getBoolean(lineElement.dataset.wowrioCarePromotionPageEligible);
  const active = mode !== 'off' && selected && available && eligible && quantity <= maxQuantity;
  const displayPrice = active
    ? mode === 'free'
      ? 0
      : Math.round((originalPrice * (100 - percentage)) / 100)
    : originalPrice;
  const customLabel = String(lineElement.dataset.wowrioCarePromotionLabel || '').trim();

  return {
    active,
    mode,
    percentage,
    originalPrice,
    displayPrice,
    hasCustomLabel: customLabel !== '',
    label: customLabel || (mode === 'percentage' ? `${percentage}% OFF` : mode === 'free' ? 'FREE' : ''),
    note: String(lineElement.dataset.wowrioCarePromotionNote || '').trim(),
  };
}

class WowrioSetupPresets extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Element | Document | null} */
  #scope;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#scope = getProductScope(this);

    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });

    this.#applyInitialSelection();
    this.#syncCards();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleClick = (event) => {
    const card = event.target instanceof Element ? event.target.closest(SETUP_CARD_SELECTOR) : null;

    if (!(card instanceof HTMLButtonElement) || card.disabled) return;

    const behavior = card.dataset.wowrioSetupBehavior || 'metadata_only';

    if (behavior === 'proxy_native_option') {
      const nativeOption = findNativeOption(this.#scope || document, card.dataset.wowrioSetupOptionName, card.dataset.wowrioSetupOptionValue);

      if (!nativeOption || isNativeOptionUnavailable(nativeOption)) {
        this.#announce('This setup is not available for the current product options.');
        return;
      }

      proxyNativeOption(nativeOption);
    }

    this.#selectCard(card);
    notifyMerchandisingChanged(this);
  };

  #handleProductSelect = (event) => {
    const productSelectEvent = /** @type {CustomEvent & { promise?: Promise<unknown> }} */ (event);

    if (productSelectEvent.promise && typeof productSelectEvent.promise.finally === 'function') {
      productSelectEvent.promise.finally(() => {
        window.requestAnimationFrame(() => this.#syncCards());
      });
      return;
    }

    window.requestAnimationFrame(() => this.#syncCards());
  };

  #applyInitialSelection() {
    const cards = this.#getCards();
    if (!cards.length || cards.some((card) => card.hasAttribute(SETUP_SELECTED_ATTRIBUTE))) return;

    const nativeSelectedCard = cards.find((card) => {
      if (card.dataset.wowrioSetupBehavior !== 'proxy_native_option') return false;

      const nativeOption = findNativeOption(
        this.#scope || document,
        card.dataset.wowrioSetupOptionName,
        card.dataset.wowrioSetupOptionValue
      );

      return nativeOption ? isNativeOptionSelected(nativeOption) : false;
    });

    if (nativeSelectedCard) {
      this.#selectCard(nativeSelectedCard, false);
      return;
    }

    const defaultCard = cards.find((card) => getBoolean(card.dataset.wowrioSetupDefaultSelected) && !card.disabled);
    if (defaultCard) {
      this.#selectCard(defaultCard, false);
    }
  }

  #syncCards() {
    for (const card of this.#getCards()) {
      if (card.dataset.wowrioSetupBehavior === 'proxy_native_option') {
        const nativeOption = findNativeOption(
          this.#scope || document,
          card.dataset.wowrioSetupOptionName,
          card.dataset.wowrioSetupOptionValue
        );
        const unavailable = !nativeOption || isNativeOptionUnavailable(nativeOption);
        card.disabled = unavailable;

        if (nativeOption && isNativeOptionSelected(nativeOption)) {
          this.#selectCard(card, false);
        }
      }

      const selected = card.hasAttribute(SETUP_SELECTED_ATTRIBUTE);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    notifyMerchandisingChanged(this);
  }

  /**
   * @param {HTMLButtonElement} selectedCard
   * @param {boolean} [announce]
   */
  #selectCard(selectedCard, announce = true) {
    for (const card of this.#getCards()) {
      const selected = card === selectedCard;
      card.toggleAttribute(SETUP_SELECTED_ATTRIBUTE, selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    if (announce) {
      const title = selectedCard.querySelector('.wowrio-setup-presets__title')?.textContent?.trim();
      if (title) this.#announce(`${title} selected.`);
    }
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getCards() {
    return Array.from(this.querySelectorAll(SETUP_CARD_SELECTOR)).filter((card) => card instanceof HTMLButtonElement);
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const status = this.querySelector('[data-wowrio-setup-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('wowrio-setup-presets')) {
  customElements.define('wowrio-setup-presets', WowrioSetupPresets);
}

if (!window[GLOBAL_LISTENER_FLAG]) {
  window[GLOBAL_LISTENER_FLAG] = true;
  window.addEventListener('submit', handleNativeProductFormSubmit, { capture: true });
  document.addEventListener(MERCHANDISING_CHANGE_EVENT, handleMerchandisingChanged);
  document.addEventListener('change', handleDocumentChange);
  document.addEventListener(StandardEvents.productSelect, handleProductSelectForMerchandising);
}

if (!window[WOWRIO_MODAL_LISTENER_FLAG]) {
  window[WOWRIO_MODAL_LISTENER_FLAG] = true;
  document.addEventListener('click', handleModalClick);
  document.addEventListener('keydown', handleModalKeydown);
  document.addEventListener('shopify:section:unload', handleModalSectionUnload);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAllProductMerchandising, { once: true });
} else {
  syncAllProductMerchandising();
}

/**
 * @param {Event} event
 */
function handleNativeProductFormSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(PRODUCT_FORM_SELECTOR)) return;

  const scope = getProductScope(form);
  syncSetupPropertyInputs(scope);

  const formData = new FormData(form);
  const mainVariantId = String(formData.get('id') || '').trim();
  const extraLines = dedupeExtraLines(collectExtraLines(scope), mainVariantId);

  if (!extraLines.length) return;
  if (!form.checkValidity()) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const productForm = form.closest('product-form-component');
  if (!(productForm instanceof HTMLElement)) return;
  if (productForm.hasAttribute('data-wowrio-submit-pending')) {
    return;
  }

  if (!mainVariantId) {
    dispatchWowrioCartError(productForm, 'Please choose an available product option.');
    return;
  }

  const mainQuantity = parseQuantity(formData.get('quantity'), Number(productForm.dataset.quantityDefault) || 1);
  const mainItem = buildMainCartItem(mainVariantId, mainQuantity, formData);
  const allItems = [mainItem, ...extraLines.map((line) => ({ id: Number(line.variantId), quantity: line.quantity }))];

  submitWowrioCartLines({
    productForm,
    items: allItems,
    event,
    productId: productForm.dataset.productId || '',
  });
}

/**
 * @param {Event} event
 */
function handleMerchandisingChanged(event) {
  if (!(event.target instanceof Element)) return;
  const scope = getProductScope(event.target);
  syncSetupPropertyInputs(scope);
  syncAcceleratedCheckout(scope);
}

/**
 * @param {Event} event
 */
function handleDocumentChange(event) {
  if (!(event.target instanceof Element)) return;
  const scope = getProductScope(event.target);

  if (scope.querySelector(EXTRA_LINE_SELECTOR) || scope.querySelector(SETUP_PRESETS_SELECTOR)) {
    syncSetupPropertyInputs(scope);
    syncAcceleratedCheckout(scope);
  }
}

/**
 * @param {Event} event
 */
function handleModalClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest(WOWRIO_MODAL_TRIGGER_SELECTOR);
  if (trigger instanceof HTMLElement) {
    const id = trigger.dataset.wowrioModalTrigger;
    const dialog = id ? document.getElementById(id) : null;

    if (dialog instanceof HTMLDialogElement) {
      openWowrioModal(trigger, dialog);
    }
    return;
  }

  const close = target.closest(WOWRIO_MODAL_CLOSE_SELECTOR);
  if (close instanceof HTMLElement) {
    const dialog = close.closest(WOWRIO_MODAL_SELECTOR);
    if (dialog instanceof HTMLDialogElement) {
      closeWowrioModal(dialog);
    }
    return;
  }

  const dialog = target.closest(WOWRIO_MODAL_SELECTOR);
  if (
    dialog instanceof HTMLDialogElement &&
    event instanceof MouseEvent &&
    isWowrioModalBackdropClick(event, dialog)
  ) {
    closeWowrioModal(dialog);
  }
}

/**
 * @param {MouseEvent} event
 * @param {HTMLDialogElement} dialog
 */
function isWowrioModalBackdropClick(event, dialog) {
  if (event.target !== dialog) return false;

  const panel = dialog.querySelector(WOWRIO_MODAL_PANEL_SELECTOR);
  if (!(panel instanceof HTMLElement)) return false;

  const bounds = panel.getBoundingClientRect();

  return (
    event.clientX < bounds.left ||
    event.clientX > bounds.right ||
    event.clientY < bounds.top ||
    event.clientY > bounds.bottom
  );
}

/**
 * @param {HTMLElement} trigger
 * @param {HTMLDialogElement} dialog
 */
function openWowrioModal(trigger, dialog) {
  if (dialog.open) return;

  wowrioModalState.set(dialog, { trigger, restoreFocus: true });
  trigger.setAttribute('aria-expanded', 'true');
  dialog.addEventListener('close', handleWowrioModalClosed, { once: true });

  try {
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
      dialog.setAttribute('data-wowrio-modal-fallback', 'true');
    }
  } catch (error) {
    console.warn('WOWRIO modal could not use showModal; using the open attribute fallback.', error);
    dialog.setAttribute('open', '');
    dialog.setAttribute('data-wowrio-modal-fallback', 'true');
  }

  if (!dialog.open) {
    trigger.setAttribute('aria-expanded', 'false');
    wowrioModalState.delete(dialog);
    return;
  }

  openWowrioModals.add(dialog);
  syncWowrioModalScrollLock();

  window.requestAnimationFrame(() => {
    const focusTarget =
      dialog.querySelector(WOWRIO_MODAL_CLOSE_SELECTOR) ||
      dialog.querySelector('[autofocus]') ||
      dialog.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
    } else {
      dialog.focus({ preventScroll: true });
    }
  });
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {boolean} restoreFocus
 */
function closeWowrioModal(dialog, restoreFocus = true) {
  const state = wowrioModalState.get(dialog);
  if (state) state.restoreFocus = restoreFocus;

  if (dialog.open && typeof dialog.close === 'function') {
    dialog.close();
    return;
  }

  dialog.removeAttribute('open');
  finishWowrioModalClose(dialog);
}

/**
 * @param {Event} event
 */
function handleWowrioModalClosed(event) {
  if (event.currentTarget instanceof HTMLDialogElement) {
    finishWowrioModalClose(event.currentTarget);
  }
}

/**
 * @param {HTMLDialogElement} dialog
 */
function finishWowrioModalClose(dialog) {
  const state = wowrioModalState.get(dialog);
  const trigger = state?.trigger;

  if (trigger instanceof HTMLElement) {
    trigger.setAttribute('aria-expanded', 'false');
  }

  dialog.removeAttribute('data-wowrio-modal-fallback');
  openWowrioModals.delete(dialog);
  wowrioModalState.delete(dialog);
  syncWowrioModalScrollLock();

  if (state?.restoreFocus && trigger instanceof HTMLElement && trigger.isConnected) {
    window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
  }
}

function syncWowrioModalScrollLock() {
  for (const dialog of openWowrioModals) {
    if (!dialog.isConnected || !dialog.open) {
      const trigger = wowrioModalState.get(dialog)?.trigger;
      if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'false');
      openWowrioModals.delete(dialog);
      wowrioModalState.delete(dialog);
    }
  }

  document.documentElement.classList.toggle(WOWRIO_MODAL_SCROLL_LOCK_CLASS, openWowrioModals.size > 0);
}

/**
 * @param {KeyboardEvent} event
 */
function handleModalKeydown(event) {
  if (event.key !== 'Escape') return;

  const fallbackDialogs = Array.from(openWowrioModals).filter((dialog) =>
    dialog.hasAttribute('data-wowrio-modal-fallback')
  );
  const dialog = fallbackDialogs[fallbackDialogs.length - 1];

  if (dialog instanceof HTMLDialogElement) {
    event.preventDefault();
    closeWowrioModal(dialog);
  }
}

/**
 * @param {Event} event
 */
function handleModalSectionUnload(event) {
  const section = event.target;
  if (!(section instanceof Element)) return;

  for (const dialog of Array.from(openWowrioModals)) {
    if (section.contains(dialog)) {
      closeWowrioModal(dialog, false);
    }
  }
}

/**
 * @param {Event} event
 */
function handleProductSelectForMerchandising(event) {
  const productSelectEvent = /** @type {CustomEvent & { promise?: Promise<unknown> }} */ (event);
  const target = event.target instanceof Element ? event.target : document;
  const scope = getProductScope(target);

  if (productSelectEvent.promise && typeof productSelectEvent.promise.finally === 'function') {
    productSelectEvent.promise.finally(() => {
      window.requestAnimationFrame(() => {
        syncSetupPropertyInputs(scope);
        syncAcceleratedCheckout(scope);
      });
    });
    return;
  }

  window.requestAnimationFrame(() => {
    syncSetupPropertyInputs(scope);
    syncAcceleratedCheckout(scope);
  });
}

function syncAllProductMerchandising() {
  const scopes = new Set();

  for (const element of document.querySelectorAll(`${EXTRA_LINE_SELECTOR}, ${SETUP_PRESETS_SELECTOR}, ${PRODUCT_FORM_SELECTOR}`)) {
    scopes.add(getProductScope(element));
  }

  for (const scope of scopes) {
    syncSetupPropertyInputs(scope);
    syncAcceleratedCheckout(scope);
  }
}

/**
 * @param {Element | Document | null} scope
 */
function syncSetupPropertyInputs(scope) {
  const form = getNativeProductForm(scope);
  if (!form) return;

  for (const input of form.querySelectorAll(SETUP_PROPERTY_INPUT_SELECTOR)) {
    input.remove();
  }

  for (const property of collectSetupProperties(scope)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${property.name}]`;
    input.value = property.value;
    input.setAttribute('data-wowrio-setup-property-input', 'true');
    form.append(input);
  }
}

/**
 * @param {Element | Document | null} scope
 */
function syncAcceleratedCheckout(scope) {
  const form = getNativeProductForm(scope);
  const container = form?.closest('product-form-component')?.querySelector(ACCELERATED_CHECKOUT_SELECTOR);
  if (!(container instanceof HTMLElement)) return;

  if (!container.hasAttribute('data-wowrio-original-hidden')) {
    container.dataset.wowrioOriginalHidden = container.hidden ? 'true' : 'false';
  }

  if (container.dataset.wowrioOriginalHidden === 'true') return;

  const mainVariantId = String(new FormData(form).get('id') || '').trim();
  const shouldHide = dedupeExtraLines(collectExtraLines(scope), mainVariantId).some((line) => line.hideAcceleratedCheckout);

  container.toggleAttribute('hidden', shouldHide);
  container.toggleAttribute('data-wowrio-accelerated-checkout-hidden', shouldHide);
}

/**
 * @param {Element | Document | null} scope
 * @returns {{ name: string, value: string }[]}
 */
function collectSetupProperties(scope) {
  const properties = [];

  for (const card of getSelectedSetupCards(scope)) {
    if (card.dataset.wowrioSetupBehavior !== 'metadata_only') continue;

    const name = String(card.dataset.wowrioSetupPropertyName || '').trim();
    const value = String(card.dataset.wowrioSetupPropertyValue || '').trim();

    if (name && value) {
      properties.push({ name, value });
    }
  }

  return properties;
}

/**
 * @param {Element | Document | null} scope
 * @returns {{ variantId: string, quantity: number, hideAcceleratedCheckout: boolean }[]}
 */
function collectExtraLines(scope) {
  const lines = [];

  for (const lineElement of (scope || document).querySelectorAll(EXTRA_LINE_SELECTOR)) {
    if (!(lineElement instanceof HTMLElement) || !isExtraLineSelected(lineElement)) continue;

    const variantId = getExtraLineVariantId(lineElement);
    if (!variantId) continue;

    lines.push({
      variantId,
      quantity: parseQuantity(lineElement.dataset.wowrioExtraQuantity, 1),
      hideAcceleratedCheckout: getBoolean(lineElement.dataset.wowrioHideAcceleratedCheckout),
    });
  }

  for (const card of getSelectedSetupCards(scope)) {
    if (card.dataset.wowrioSetupBehavior !== 'select_extra_product_lines') continue;

    const presets = card.closest(SETUP_PRESETS_SELECTOR);
    const hideAcceleratedCheckout = getBoolean(presets?.dataset.wowrioHideAcceleratedCheckout);

    for (const extraLine of card.querySelectorAll(SETUP_EXTRA_LINE_SELECTOR)) {
      if (!(extraLine instanceof HTMLElement) || !getBoolean(extraLine.dataset.wowrioExtraAvailable)) continue;

      const variantId = String(extraLine.dataset.wowrioExtraVariantId || '').trim();
      if (!variantId) continue;

      lines.push({
        variantId,
        quantity: parseQuantity(extraLine.dataset.wowrioExtraQuantity, 1),
        hideAcceleratedCheckout,
      });
    }
  }

  return lines;
}

/**
 * @param {{ variantId: string, quantity: number, hideAcceleratedCheckout: boolean }[]} lines
 * @param {string} mainVariantId
 */
function dedupeExtraLines(lines, mainVariantId) {
  const deduped = new Map();

  for (const line of lines) {
    if (!line.variantId || line.variantId === mainVariantId) continue;

    const existing = deduped.get(line.variantId);
    if (existing) {
      existing.quantity = Math.max(existing.quantity, line.quantity);
      existing.hideAcceleratedCheckout = existing.hideAcceleratedCheckout || line.hideAcceleratedCheckout;
      continue;
    }

    deduped.set(line.variantId, { ...line });
  }

  return Array.from(deduped.values());
}

/**
 * @param {Element | Document | null} scope
 * @returns {HTMLButtonElement[]}
 */
function getSelectedSetupCards(scope) {
  return Array.from((scope || document).querySelectorAll(`${SETUP_CARD_SELECTOR}[${SETUP_SELECTED_ATTRIBUTE}]`)).filter(
    (card) => card instanceof HTMLButtonElement && !card.disabled
  );
}

/**
 * @param {HTMLElement} lineElement
 */
function isExtraLineSelected(lineElement) {
  if (!getBoolean(lineElement.dataset.wowrioExtraAvailable)) return false;
  if (lineElement.dataset.wowrioCareMode === 'informational_only') return false;
  if (lineElement.dataset.wowrioCareMode === 'included_product') return true;

  const toggle = lineElement.querySelector(EXTRA_TOGGLE_SELECTOR);
  return toggle instanceof HTMLInputElement ? toggle.checked : false;
}

/**
 * @param {HTMLElement} lineElement
 */
function getExtraLineVariantId(lineElement) {
  const select = lineElement.querySelector(EXTRA_VARIANT_SELECT_SELECTOR);
  if (select instanceof HTMLSelectElement && select.value) return select.value;

  return String(lineElement.dataset.wowrioExtraVariantId || '').trim();
}

/**
 * @param {string} variantId
 * @param {number} quantity
 * @param {FormData} formData
 */
function buildMainCartItem(variantId, quantity, formData) {
  const item = {
    id: Number(variantId),
    quantity,
  };

  const properties = collectPropertiesFromFormData(formData);
  if (Object.keys(properties).length) {
    item.properties = properties;
  }

  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) {
    item.selling_plan = Number(sellingPlan);
  }

  return item;
}

/**
 * @param {FormData} formData
 * @returns {Record<string, string>}
 */
function collectPropertiesFromFormData(formData) {
  const properties = {};

  for (const [key, value] of formData.entries()) {
    if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
    if (value instanceof File) continue;

    const propertyName = key.slice('properties['.length, -1);
    const propertyValue = String(value || '').trim();

    if (propertyName && propertyValue) {
      properties[propertyName] = propertyValue;
    }
  }

  return properties;
}

/**
 * @param {{
 *   productForm: HTMLElement,
 *   items: Array<{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }>,
 *   event: Event,
 *   productId: string
 * }} config
 */
function submitWowrioCartLines({ productForm, items, event, productId }) {
  productForm.setAttribute('data-wowrio-submit-pending', 'true');
  setNativeButtonsDisabled(productForm, true);
  clearNativeFormError(productForm);

  const cartItemComponentsSectionIds = getCartItemComponentSectionIds();
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  productForm.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      promise: deferredEventPromise.promise,
    })
  );

  const payload = {
    items,
    sections: cartItemComponentsSectionIds.join(','),
    sections_url: window.location.pathname,
  };

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => response.json())
    .then(async (response) => {
      if (response.status) {
        const message = response.message || response.description || 'Add to cart failed';
        showNativeFormError(productForm, message);
        productForm.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: {
              description: response.description,
              errors: response.errors,
            },
          })
        );

        return fetchCart()
          .then((cart) =>
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                didError: true,
                items: cart.items,
                source: 'wowrio-native-product-enhancements',
                sourceId: productForm.id || 'wowrio-product-form-extension',
                itemCount: totalQuantity,
                productId,
              },
            })
          )
          .catch(deferredEventPromise.reject);
      }

      clearNativeFormError(productForm);
      announceNativeFormSuccess(productForm);

      const cart = await fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'wowrio-native-product-enhancements',
          sourceId: productForm.id || 'wowrio-product-form-extension',
          itemCount: totalQuantity,
          productId,
          sections: response.sections,
        },
      });
    })
    .catch((error) => {
      console.error(error);
      deferredEventPromise.reject(error);
      dispatchWowrioCartError(productForm, error?.message || 'Network error during add to cart');
    })
    .finally(() => {
      productForm.removeAttribute('data-wowrio-submit-pending');
      setNativeButtonsDisabled(productForm, false);
    });
}

/**
 * @param {HTMLElement} productForm
 * @param {string} message
 */
function dispatchWowrioCartError(productForm, message) {
  showNativeFormError(productForm, message);
  productForm.dispatchEvent(
    new CartErrorEvent({
      error: message,
      code: 'INVALID',
    })
  );
}

/**
 * @param {HTMLElement} productForm
 * @param {string} message
 */
function showNativeFormError(productForm, message) {
  const errorElement = productForm.querySelector('[ref="addToCartTextError"]');
  if (errorElement instanceof HTMLElement) {
    errorElement.classList.remove('hidden');
    let messageElement = errorElement.querySelector('[data-wowrio-add-error-message]');

    if (!messageElement) {
      messageElement = document.createElement('span');
      messageElement.setAttribute('data-wowrio-add-error-message', 'true');
      errorElement.append(messageElement);
    }

    messageElement.textContent = message;
  }

  setNativeLiveRegion(productForm, message);
}

/**
 * @param {HTMLElement} productForm
 */
function clearNativeFormError(productForm) {
  const errorElement = productForm.querySelector('[ref="addToCartTextError"]');
  if (errorElement instanceof HTMLElement) {
    errorElement.classList.add('hidden');
  }
}

/**
 * @param {HTMLElement} productForm
 */
function announceNativeFormSuccess(productForm) {
  const addedText =
    productForm.querySelector('.add-to-cart-text--added')?.textContent?.trim() || window.Theme?.translations?.added || 'Added';
  setNativeLiveRegion(productForm, addedText);
  window.setTimeout(() => setNativeLiveRegion(productForm, ''), 5000);
}

/**
 * @param {HTMLElement} productForm
 * @param {string} message
 */
function setNativeLiveRegion(productForm, message) {
  const liveRegion = productForm.querySelector('[ref="liveRegion"]');
  if (liveRegion) {
    liveRegion.textContent = message;
  }
}

/**
 * @param {HTMLElement} productForm
 * @param {boolean} disabled
 */
function setNativeButtonsDisabled(productForm, disabled) {
  for (const button of productForm.querySelectorAll('[ref="addToCartButton"]')) {
    if (button instanceof HTMLButtonElement) {
      button.disabled = disabled;
    }
  }
}

/**
 * @returns {string[]}
 */
function getCartItemComponentSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

  return response.json();
}

/**
 * @param {Element | Document | null} scope
 * @returns {HTMLFormElement | null}
 */
function getNativeProductForm(scope) {
  return (scope || document).querySelector(PRODUCT_FORM_SELECTOR);
}

/**
 * @param {Element} element
 * @returns {Element | Document}
 */
function getProductScope(element) {
  return element.closest('.shopify-section') || element.closest('section') || document;
}

/**
 * @param {unknown} value
 * @param {number} fallback
 * @returns {number}
 */
function parseQuantity(value, fallback) {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : fallback;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function getBoolean(value) {
  return value === true || value === 'true';
}

/**
 * @param {Element | Document} scope
 * @param {string | undefined} optionName
 * @param {string | undefined} optionValue
 * @returns {HTMLInputElement | HTMLOptionElement | null}
 */
function findNativeOption(scope, optionName, optionValue) {
  const targetName = normalizeOptionValue(optionName);
  const targetValue = normalizeOptionValue(optionValue);

  if (!targetName || !targetValue) return null;

  const picker = scope.querySelector('variant-picker');
  if (!picker) return null;

  return (
    Array.from(picker.querySelectorAll(NATIVE_OPTION_SELECTOR)).find((nativeOption) => {
      if (!(nativeOption instanceof HTMLInputElement || nativeOption instanceof HTMLOptionElement)) return false;

      return (
        normalizeOptionValue(nativeOption.dataset.optionName) === targetName &&
        normalizeOptionValue(nativeOption.value) === targetValue
      );
    }) || null
  );
}

/**
 * @param {HTMLInputElement | HTMLOptionElement} nativeOption
 */
function proxyNativeOption(nativeOption) {
  if (nativeOption instanceof HTMLInputElement) {
    nativeOption.click();
    return;
  }

  if (nativeOption.parentElement instanceof HTMLSelectElement) {
    nativeOption.parentElement.value = nativeOption.value;
    nativeOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * @param {HTMLInputElement | HTMLOptionElement} nativeOption
 * @returns {boolean}
 */
function isNativeOptionSelected(nativeOption) {
  if (nativeOption instanceof HTMLInputElement) return nativeOption.checked;

  return nativeOption.selected || nativeOption.parentElement?.value === nativeOption.value;
}

/**
 * @param {HTMLInputElement | HTMLOptionElement} nativeOption
 * @returns {boolean}
 */
function isNativeOptionUnavailable(nativeOption) {
  if (nativeOption instanceof HTMLInputElement) {
    return nativeOption.disabled || nativeOption.getAttribute('aria-disabled') === 'true';
  }

  return nativeOption.disabled;
}

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function normalizeOptionValue(value) {
  return String(value || '').trim();
}

/**
 * @param {Element} element
 */
function notifyMerchandisingChanged(element) {
  element.dispatchEvent(
    new CustomEvent(MERCHANDISING_CHANGE_EVENT, {
      bubbles: true,
    })
  );
}

class WowrioRollbackPurchaseSummary extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Element | Document | null} */
  #scope;

  /** @type {{ id: string, title: string, price: number, available: boolean }[]} */
  #variants = [];

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#scope = getProductScope(this);
    this.#variants = parseRollbackVariantData(this);

    this.#scope?.addEventListener('change', this.#handleUpdate, { signal: this.#abortController.signal });
    this.#scope?.addEventListener('input', this.#handleUpdate, { signal: this.#abortController.signal });
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleUpdate, {
      signal: this.#abortController.signal,
    });
    this.#scope?.addEventListener(MERCHANDISING_CHANGE_EVENT, this.#handleUpdate, {
      signal: this.#abortController.signal,
    });

    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleUpdate = () => {
    window.requestAnimationFrame(() => this.#update());
  };

  #update() {
    const state = buildRollbackSummaryState(this.#scope, this.#variants, {
      productTitle: this.dataset.wowrioProductTitle || '',
      moneyFormat: this.dataset.wowrioMoneyFormat || '',
    });

    setRollbackText(this, '[data-wowrio-rollback-summary-main-title]', state.main.title);
    setRollbackText(this, '[data-wowrio-rollback-summary-main-meta]', formatRollbackMainMeta(state.main));
    setRollbackText(this, '[data-wowrio-rollback-summary-main-price]', formatRollbackMoney(state.main.price * state.main.quantity, state.moneyFormat));
    setRollbackText(this, '[data-wowrio-rollback-summary-total]', formatRollbackMoney(state.totalPrice, state.moneyFormat));
    this.#renderExtras(state);
  }

  /**
   * @param {ReturnType<typeof buildRollbackSummaryState>} state
   */
  #renderExtras(state) {
    const list = this.querySelector('[data-wowrio-rollback-summary-lines]');
    const empty = this.querySelector('[data-wowrio-rollback-summary-empty]');

    if (!(list instanceof HTMLElement)) return;

    list.replaceChildren();

    if (!state.extras.length) {
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    if (empty instanceof HTMLElement) empty.hidden = true;

    for (const line of state.extras) {
      const item = document.createElement('li');
      item.className = 'wowrio-rollback-summary__line';

      const text = document.createElement('span');
      text.className = 'wowrio-rollback-summary__line-text';

      const title = document.createElement('span');
      title.className = 'wowrio-rollback-summary__line-title';
      title.textContent = line.title || 'Selected extra';

      const meta = document.createElement('span');
      meta.className = 'wowrio-rollback-summary__line-meta';
      meta.textContent = line.quantity > 1 ? `Qty ${line.quantity}` : 'Selected';
      text.append(title, meta);

      const value = document.createElement('span');
      value.className = 'wowrio-rollback-summary__line-value';

      if (line.promotionActive) {
        value.classList.add('wowrio-rollback-summary__line-value--promotional');

        const original = document.createElement('s');
        original.className = 'wowrio-rollback-summary__original-price';
        original.textContent = formatRollbackMoney(line.originalPrice * line.quantity, state.moneyFormat);

        const promotional = document.createElement('strong');
        promotional.className = 'wowrio-rollback-summary__promotional-price';
        promotional.textContent = line.promotionMode === 'free'
          ? 'FREE'
          : formatRollbackMoney(line.displayPrice * line.quantity, state.moneyFormat);

        value.append(original, promotional);

        if (line.promotionLabel && (line.promotionMode !== 'free' || line.promotionHasCustomLabel)) {
          const label = document.createElement('span');
          label.className = 'wowrio-rollback-summary__promotion-label';
          label.textContent = line.promotionLabel;
          text.append(label);
        }

        if (line.promotionNote) {
          const note = document.createElement('span');
          note.className = 'wowrio-rollback-summary__promotion-note';
          note.textContent = line.promotionNote;
          text.append(note);
        }
      } else {
        value.textContent = formatRollbackMoney(line.price * line.quantity, state.moneyFormat);
      }

      item.append(text, value);
      list.append(item);
    }
  }
}

if (!customElements.get('wowrio-rollback-purchase-summary')) {
  customElements.define('wowrio-rollback-purchase-summary', WowrioRollbackPurchaseSummary);
}

/**
 * @param {Element | Document | null} scope
 * @param {{ id: string, title: string, price: number, available: boolean }[]} variants
 * @param {{ productTitle: string, moneyFormat: string }} config
 */
function buildRollbackSummaryState(scope, variants, config) {
  const form = getNativeProductForm(scope);
  const formData = form ? new FormData(form) : null;
  const mainVariantId = String(formData?.get('id') || '').trim();
  const mainVariant = variants.find((variant) => variant.id === mainVariantId) || variants[0] || null;
  const mainQuantity = parseQuantity(formData?.get('quantity'), 1);
  const mainPrice = Number(mainVariant?.price) || 0;
  const extras = collectRollbackSummaryExtraLines(scope, mainVariantId);
  const extrasTotal = extras.reduce((sum, line) => sum + line.displayPrice * line.quantity, 0);

  return {
    moneyFormat: config.moneyFormat,
    main: {
      title: config.productTitle || '',
      variantTitle: getRollbackDisplayVariantTitle(mainVariant?.title || ''),
      quantity: mainQuantity,
      price: mainPrice,
    },
    extras,
    totalPrice: mainPrice * mainQuantity + extrasTotal,
  };
}

/**
 * @param {Element | Document | null} scope
 * @param {string} mainVariantId
 */
function collectRollbackSummaryExtraLines(scope, mainVariantId) {
  const lines = [];
  const seen = new Map();

  for (const lineElement of (scope || document).querySelectorAll(EXTRA_LINE_SELECTOR)) {
    if (!(lineElement instanceof HTMLElement) || !isExtraLineSelected(lineElement)) continue;

    const variantId = getExtraLineVariantId(lineElement);
    if (!variantId || variantId === mainVariantId) continue;

    const quantity = parseQuantity(lineElement.dataset.wowrioExtraQuantity, 1);
    const existing = seen.get(variantId);
    const price = getRollbackExtraLinePrice(lineElement);
    const promotion = getCarePromotionState(lineElement, price);
    const next = {
      variantId,
      title: getRollbackExtraLineTitle(lineElement),
      quantity,
      price,
      displayPrice: promotion.active ? promotion.displayPrice : price,
      originalPrice: price,
      promotionActive: promotion.active,
      promotionMode: promotion.mode,
      promotionLabel: promotion.label,
      promotionHasCustomLabel: promotion.hasCustomLabel,
      promotionNote: promotion.active ? promotion.note : '',
    };

    if (existing) {
      existing.quantity = Math.max(existing.quantity, quantity);
      continue;
    }

    seen.set(variantId, next);
    lines.push(next);
  }

  return lines;
}

/**
 * @param {HTMLElement} lineElement
 */
function getRollbackExtraLineTitle(lineElement) {
  const title = String(lineElement.dataset.wowrioExtraTitle || '').trim();
  const select = lineElement.querySelector(EXTRA_VARIANT_SELECT_SELECTOR);
  const selectedText =
    select instanceof HTMLSelectElement ? getRollbackDisplayVariantTitle(select.selectedOptions[0]?.textContent || '') : '';

  if (title && selectedText) return `${title} — ${selectedText}`;
  return title || selectedText;
}

/**
 * @param {HTMLElement} lineElement
 */
function getRollbackExtraLinePrice(lineElement) {
  const select = lineElement.querySelector(EXTRA_VARIANT_SELECT_SELECTOR);

  if (select instanceof HTMLSelectElement) {
    const selectedPrice = select.selectedOptions[0]?.dataset.wowrioExtraPrice;
    if (selectedPrice !== undefined) return parseRollbackPrice(selectedPrice);
  }

  return parseRollbackPrice(lineElement.dataset.wowrioExtraPrice);
}

/**
 * @param {HTMLElement} summary
 */
function parseRollbackVariantData(summary) {
  const script = summary.querySelector('[data-wowrio-rollback-main-variants-json]');

  if (!(script instanceof HTMLScriptElement)) return [];

  try {
    const variants = JSON.parse(script.textContent || '[]');
    if (!Array.isArray(variants)) return [];

    return variants.map((variant) => ({
      id: String(variant?.id || ''),
      title: String(variant?.title || ''),
      price: parseRollbackPrice(variant?.price),
      available: Boolean(variant?.available),
    }));
  } catch (error) {
    console.warn('WOWRIO rollback purchase summary could not read variant data.', error);
    return [];
  }
}

/**
 * @param {{ variantTitle: string, quantity: number }} main
 */
function formatRollbackMainMeta(main) {
  const parts = [];

  if (main.variantTitle) {
    parts.push(main.variantTitle);
  }

  parts.push(`Qty ${main.quantity}`);

  return parts.join(' · ');
}

/**
 * @param {Element} scope
 * @param {string} selector
 * @param {string} value
 */
function setRollbackText(scope, selector, value) {
  const element = scope.querySelector(selector);
  if (element) element.textContent = value;
}

/**
 * @param {string} title
 */
function getRollbackDisplayVariantTitle(title) {
  const trimmed = String(title || '').replace(/\s+/g, ' ').trim();
  return trimmed && trimmed !== 'Default Title' ? trimmed : '';
}

/**
 * @param {unknown} value
 */
function parseRollbackPrice(value) {
  const price = Number(value);
  return Number.isFinite(price) ? price : 0;
}

/**
 * @param {number} cents
 * @param {string} moneyFormat
 */
function formatRollbackMoney(cents, moneyFormat) {
  const format = moneyFormat || '${{amount}}';
  const amount = Number(cents) || 0;

  return format
    .replace(/\{\{\s*amount\s*\}\}/g, formatRollbackMoneyAmount(amount, 2, '.', ','))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formatRollbackMoneyAmount(amount, 0, '.', ','))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatRollbackMoneyAmount(amount, 2, ',', '.'))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, formatRollbackMoneyAmount(amount, 0, ',', '.'))
    .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, formatRollbackMoneyAmount(amount, 2, '.', "'"));
}

/**
 * @param {number} cents
 * @param {number} precision
 * @param {string} decimal
 * @param {string} thousands
 */
function formatRollbackMoneyAmount(cents, precision, decimal, thousands) {
  const value = (cents / 100).toFixed(precision);
  const parts = value.split('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

  return parts.join(decimal);
}
